import { Box } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { RouteComponentProps } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { API } from 'api';
import { getPageStatus, PageLayout } from 'components/PageLayout';
import { InlineStatus } from 'components/InlineStatus';
import { ProfileLayout } from 'components/ProfileLayout';
import { User } from 'shared/types';
import * as wildfireIcons from 'features/mapping/icons/iconData/wildfireIcons';

type ContentProps = {
  user: User;
};

// todo: react-query function type returns can be addressed after react-query 5 upgrade
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const fetchUserProfile = (userId: string) => {
  return API.get(`users/${userId}`);
};

const Bio = ({ children }: { children: string }): JSX.Element => (
  <Box
    sx={(theme) => ({
      '& > *': {
        fontSize: theme.typography.body1.fontSize,
        color: theme.palette.text.primary,
      },
      // Markdown elements carry their own block margins; the surrounding
      // layout owns the spacing, so drop the outer ones.
      '& > *:first-of-type': {
        marginTop: 0,
      },
      '& > *:last-of-type': {
        marginBottom: 0,
      },
      '& a': {
        color: theme.palette.text.link,
      },
    })}
  >
    <ReactMarkdown>{children}</ReactMarkdown>
  </Box>
);

export const Content = (props: ContentProps): JSX.Element => {
  const { user } = props;

  if (!user.userprofile) {
    return <InlineStatus status="error" fullWidth />;
  }

  return (
    <ProfileLayout
      documentTitle={user.displayName}
      imageUrl={user.userprofile.avatarUrl}
      imageFallbackUrl={wildfireIcons.ActiveFireIcon.data}
      imageAlt={user.displayName}
      title={user.displayName}
      subtitle={user.userprofile.headline}
      isVerified={user.isVerified}
    >
      {user.userprofile.bio && <Bio>{user.userprofile.bio}</Bio>}
    </ProfileLayout>
  );
};

export const UserProfile = (
  props: RouteComponentProps<{ userId: string }>,
): JSX.Element => {
  const {
    match: {
      params: { userId },
    },
  } = props;
  const query = useQuery({
    queryKey: ['user', userId],
    queryFn: () => fetchUserProfile(userId),
  });

  const { data, isLoading, isError } = query;

  const status = getPageStatus({
    isLoading,
    isError: isError || !data,
  });

  return (
    <PageLayout
      data-cname="UserProfilePage"
      status={status}
      headerBarProps={{ action: status === 'ready' ? '/' : 'back' }}
    >
      {data && <Content user={data.data as User} />}
    </PageLayout>
  );
};
